import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { DatePicker } from '../../components/DatePicker/DatePicker';
import {
  isDateValidByBatching,
  getNextAvailableDateByBatching,
  BatchingSettings,
} from '../../utils/batching';
interface IProps {
  onConfirm: ({
    nextBillingDate,
  }: {
    nextBillingDate: string;
  }) => Promise<void>;
  setOpen: (value: boolean) => void;
  open: boolean;
  shopConfiguration?: ShopConfiguration;
  batchingSettings?: BatchingSettings;
}

export default ({
  open,
  setOpen,
  onConfirm,
  shopConfiguration,
  batchingSettings,
}: IProps): React.ReactElement => {
  const nextBatchingAvailableDate = useMemo(
    () => getNextAvailableDateByBatching({ batchingSettings }),
    [batchingSettings],
  );

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [datePickerValue, setDatePickerValue] = useState<dayjs.Dayjs>(
    nextBatchingAvailableDate,
  );

  const closeModal = () => {
    setDatePickerValue(nextBatchingAvailableDate);
    setOpen(false);
  };

  const handleDatePickerChange = (value: string | Date) => {
    if (value) {
      setDatePickerValue(dayjs(value));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await onConfirm({ nextBillingDate: datePickerValue.toISOString() });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  return (
    <>
      <Modal isOpen={open} onClose={closeModal} isCentered>
        <ModalOverlay className="awt-reactivate-modal--overlay">
          <ModalContent className="awt-reactivate-modal--content">
            <ModalHeader className="awt-reactivate-modal--title">
              {t('reactivateModal.title')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody className="awt-reactivate-modal--body">
              <Box w="100%" p={4}>
                <Text>{t('reactivateModal.body')}</Text>
                <form
                  style={{ marginBottom: 100 }}
                  id="bundle_reactivate_form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <DatePicker
                    placeholderText={t('reactivateModal.placeholder')}
                    timezone={shopConfiguration?.ShopDomainTZ}
                    value={datePickerValue.toDate()}
                    onChange={handleDatePickerChange}
                    minDate={nextBatchingAvailableDate.toDate()}
                    required
                    filterDate={(date) =>
                      isDateValidByBatching({ date, batchingSettings })
                    }
                  />
                </form>
              </Box>
            </ModalBody>

            <ModalFooter className="awt-reactivate-modal--footer">
              <Button mr="4" onClick={closeModal} variant="outlinedBig">
                {t('reactivateModal.neverMind')}
              </Button>
              <Button
                isLoading={loading}
                disabled={
                  !datePickerValue ||
                  !isDateValidByBatching({
                    date: datePickerValue.toDate(),
                    batchingSettings,
                  })
                }
                onClick={handleFormSubmit}
                variant="blackBig"
              >
                {t('reactivateModal.save')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};
